/**
 * AlertingPage Component
 * Extracted from App.jsx - Phase 1 Refactoring
 * Migrated to iio-token design system (2026-05-23)
 */
import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api, getToken } from "../utils/helpers";

// Animierter Doppel-Chevron ">>" (neue CI, wie in der Pipeline) statt "→".
const ARROW=<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 4 16 12 8 20"/></svg>;

// Severity-Icons fuer den Modal-Banner.
const SEV_ICONS={
  critical:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  warning:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  info:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
};

// ServiceNow Standard-Kategorien (Fallback, wenn die Instanz nicht erreichbar ist).
// Werte = echte OOB-Choice-Values von incident.category (kleingeschrieben englisch),
// die Beschriftung kommt aus dem Katalog (snow.kategorien.<wert>).
const SNOW_OOB_CATEGORIES=[
  {value:"inquiry"},
  {value:"software"},
  {value:"hardware"},
  {value:"network"},
  {value:"database"},
];

const AlertingPage=({spaces,onNavigate})=>{
  const{t,i18n}=useTranslation(["alerting","gemeinsam"]);
  const gebiet=i18n.language==="en"?"en-GB":"de-DE";
  // Beschriftung einer ServiceNow-Kategorie: echte Auswahlwerte bringen ihre
  // eigene Beschriftung mit, der Fallback holt sie aus dem Katalog.
  const snowKatLabel=(c)=>c.label||t(`snow.kategorien.${c.value}`,{defaultValue:c.value});
  // Beschriftung der Zeitwerte. Der Wert selbst bleibt die Minutenzahl.
  const min=(n)=>t("zeit.minuten",{count:n});
  const std=(n)=>t("zeit.stunden",{count:n});
  const[tab,setTab]=useState("alerts"); // "alerts" | "reprocessing"
  const[alerts,setAlerts]=useState([]);const[instances,setInstances]=useState([]);
  const[loading,setLoading]=useState(true);
  const[editAlert,setEditAlert]=useState(null);
  const[editEmails,setEditEmails]=useState("");const[editUseAi,setEditUseAi]=useState(true);const[editCustomText,setEditCustomText]=useState("");
  const[editSeverity,setEditSeverity]=useState("critical");const[editEvalWindow,setEditEvalWindow]=useState(15);
  const[editReNotify,setEditReNotify]=useState(30);const[editAutoResolve,setEditAutoResolve]=useState(60);
  const[editChEmail,setEditChEmail]=useState(true);const[editChEmailOpen,setEditChEmailOpen]=useState(false);
  const[editChSnow,setEditChSnow]=useState(false);const[editChSnowOpen,setEditChSnowOpen]=useState(false);
  const[editSnowCategory,setEditSnowCategory]=useState("Integration");const[editSnowSubcat,setEditSnowSubcat]=useState("");
  const[editSnowGroup,setEditSnowGroup]=useState("");const[editSnowImpact,setEditSnowImpact]=useState("2");
  const[editSnowUrgency,setEditSnowUrgency]=useState("2");const[editSnowCaller,setEditSnowCaller]=useState("");
  const[editResolvedNotify,setEditResolvedNotify]=useState(true);const[editHtmlFormat,setEditHtmlFormat]=useState(true);
  const[editSubjectTpl,setEditSubjectTpl]=useState("[{severity}] {iflow_name} - {error_count} Fehler");
  const[saving,setSaving]=useState(false);
  // Neuer-Alert Wizard
  const[awOpen,setAwOpen]=useState(false);
  const[awInst,setAwInst]=useState(null);
  const[awPackages,setAwPackages]=useState([]);
  const[awLoadingPkgs,setAwLoadingPkgs]=useState(false);
  const[awSearch,setAwSearch]=useState("");
  // ServiceNow echte Auswahlwerte (Kategorien/Gruppen aus der Instanz)
  const[snowChoices,setSnowChoices]=useState(null);
  const[snowChoicesLoading,setSnowChoicesLoading]=useState(false);
  // Reprocessing wizard
  const[rpWizard,setRpWizard]=useState(false);
  const[rpInst,setRpInst]=useState(null);
  const[rpPackages,setRpPackages]=useState([]);
  const[rpLoadingPkgs,setRpLoadingPkgs]=useState(false);
  const[rpExpandedPkg,setRpExpandedPkg]=useState(null);
  const[rpDownloading,setRpDownloading]=useState(null);
  const[rpInjecting,setRpInjecting]=useState(null);
  const[rpDryRunResult,setRpDryRunResult]=useState(null);
  const[rpInjectResult,setRpInjectResult]=useState(null);
  const[rpStatusCache,setRpStatusCache]=useState({});
  const[rpView,setRpView]=useState("setup"); // "setup" | "failed"
  const[rpFailedMsgs,setRpFailedMsgs]=useState([]);
  const[rpLoadingFailed,setRpLoadingFailed]=useState(false);
  const[rpFailedInst,setRpFailedInst]=useState(null);
  const[rpRetrying,setRpRetrying]=useState(null);
  const[rpExpandedMsg,setRpExpandedMsg]=useState(null);
  const[rpAutoDeploy,setRpAutoDeploy]=useState(false);

  // Load all instances + their alerts
  useEffect(()=>{
    (async()=>{
      setLoading(true);
      try{
        // Load all instances from all spaces
        const allInst=[];
        for(const sp of spaces){
          try{const insts=await api(`/api/spaces/${sp.id}/instances`);insts.forEach(i=>{i.spaceName=sp.name;i.spaceId=sp.id;});allInst.push(...insts);}catch{}
        }
        setInstances(allInst);
        // Load alerts per instance
        const allAlerts=[];
        for(const inst of allInst){
          try{const al=await api(`/api/${inst.id}/alerts`);al.forEach(a=>{a.instanceName=inst.name;a.spaceName=inst.spaceName;a.tenantUrl=inst.tenantUrl;});allAlerts.push(...al);}catch{}
        }
        setAlerts(allAlerts);
      }catch{}
      setLoading(false);
    })();
  },[spaces]);

  const deleteAlert=async(alert)=>{
    if(!window.confirm(t("meldung.alertLoeschenFrage",{name:alert.iflowName})))return;
    try{await api(`/api/${alert.instanceId}/alerts/${alert.id}`,{method:"DELETE"});setAlerts(p=>p.filter(a=>a.id!==alert.id));}catch(e){window.alert(t("meldung.fehler",{meldung:e.message}));}
  };
  const toggleAlert=async(alert)=>{
    try{await api(`/api/${alert.instanceId}/alerts/${alert.id}`,{method:"PUT",body:JSON.stringify({enabled:!alert.enabled})});
    setAlerts(p=>p.map(a=>a.id===alert.id?{...a,enabled:!a.enabled}:a));}catch{}
  };
  const openEdit=(alert)=>{setEditAlert(alert);setEditEmails(alert.emails.join(", "));setEditUseAi(alert.useAi);setEditCustomText(alert.customText||"");
    setEditSeverity(alert.severity||"critical");setEditEvalWindow(alert.evalWindowMin||15);setEditReNotify(alert.reNotifyMin||30);setEditAutoResolve(alert.autoResolveMin||60);
    const ch=alert.channels||{};
    setEditChEmail(ch.email?.enabled!==false);setEditChEmailOpen(false);
    setEditChSnow(!!ch.servicenow?.enabled);setEditChSnowOpen(false);
    setEditSnowCategory(ch.servicenow?.category||"");setEditSnowSubcat(ch.servicenow?.subcategory||"");
    loadSnowChoices();
    setEditSnowGroup(ch.servicenow?.assignment_group||"");setEditSnowImpact(ch.servicenow?.impact||"2");
    setEditSnowUrgency(ch.servicenow?.urgency||"2");setEditSnowCaller(ch.servicenow?.caller_id||"");
    setEditResolvedNotify(ch.email?.resolvedNotify!==false);setEditHtmlFormat(ch.email?.htmlFormat!==false);
    setEditSubjectTpl(ch.email?.subjectTemplate||"[{severity}] {iflow_name} - {error_count} Fehler");
  };

  // Reprocessing wizard functions
  const rpSelectInst=async(inst)=>{
    setRpInst(inst);setRpLoadingPkgs(true);setRpPackages([]);setRpExpandedPkg(null);setRpStatusCache({});
    try{
      const pkgs=await api(`/api/${inst.id}/content/packages`);
      const withFlows=[];
      for(const pkg of pkgs){
        try{
          const flows=await api(`/api/${inst.id}/content/packages/${encodeURIComponent(pkg.Id)}/flows`);
          withFlows.push({...pkg,flows:flows||[]});
        }catch{withFlows.push({...pkg,flows:[]});}
      }
      setRpPackages(withFlows);
    }catch(e){console.error(e);}
    setRpLoadingPkgs(false);
  };
  const rpDownloadZip=async(inst,flowId,flowName)=>{
    setRpDownloading(flowId);
    try{
      const token=getToken();
      if(!token){window.alert(t("meldung.nichtAngemeldet"));setRpDownloading(null);return;}
      const resp=await fetch(`/api/${inst.id}/reprocess/download-zip/${encodeURIComponent(flowId)}`,{
        headers:{"Authorization":`Bearer ${token}`}
      });
      if(!resp.ok){
        let errMsg=`HTTP ${resp.status}`;
        try{const j=await resp.json();errMsg=j.error||errMsg;}catch{errMsg=await resp.text().catch(()=>errMsg);}
        throw new Error(errMsg);
      }
      const blob=await resp.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download=`${flowName||flowId}.zip`;a.click();URL.revokeObjectURL(url);
    }catch(e){window.alert(t("meldung.downloadFehler",{meldung:e.message}));}
    setRpDownloading(null);
  };
  const rpCheckStatus=async(inst,flowId)=>{
    try{const resp=await api(`/api/${inst.id}/reprocess/status/${encodeURIComponent(flowId)}`);setRpStatusCache(p=>({...p,[flowId]:resp}));return resp;}catch{return null;}
  };
  const rpDryRun=async(inst,flowId)=>{
    setRpInjecting(flowId);setRpDryRunResult(null);
    try{const resp=await api(`/api/${inst.id}/reprocess/inject/${encodeURIComponent(flowId)}?dryRun=true`,{method:"POST"});setRpDryRunResult({flowId,...resp});}
    catch(e){setRpDryRunResult({flowId,success:false,error:typeof e==="object"&&e.message?e.message:typeof e==="string"?e:JSON.stringify(e)});}
    setRpInjecting(null);
  };
  const rpInject=async(inst,flowId,deploy)=>{
    setRpInjecting(flowId);setRpInjectResult(null);
    try{const resp=await api(`/api/${inst.id}/reprocess/inject/${encodeURIComponent(flowId)}${deploy?"?deploy=true":""}`,{method:"POST"});
      setRpInjectResult({flowId,...resp});setRpStatusCache(p=>({...p,[flowId]:{injected:true,hasPersistScript:true,hasExceptionHandler:true}}));
    }catch(e){setRpInjectResult({flowId,success:false,error:typeof e==="object"&&e.message?e.message:typeof e==="string"?e:JSON.stringify(e)});}
    setRpInjecting(null);
  };
  const rpLoadFailed=async(inst)=>{
    setRpFailedInst(inst);setRpLoadingFailed(true);setRpFailedMsgs([]);
    try{const resp=await api(`/api/${inst.id}/reprocess/failed-messages?top=100`);setRpFailedMsgs(resp.entries||[]);}catch(e){console.error(e);}
    setRpLoadingFailed(false);
  };
  const rpRetryMsg=async(inst,entryId)=>{
    setRpRetrying(entryId);
    try{const resp=await api(`/api/${inst.id}/reprocess/retry/${encodeURIComponent(entryId)}`,{method:"POST"});
      if(resp.success){setRpFailedMsgs(p=>p.filter(m=>m.id!==entryId));window.alert(t("meldung.reprocessOk",{status:resp.status}));}
      else{window.alert(t("meldung.reprocessFehlgeschlagen",{status:resp.status,statusText:resp.statusText,details:resp.responseBody?.substring(0,300)||""}));}
    }catch(e){window.alert(t("meldung.reprocessFehler",{meldung:e.message||e}));}
    setRpRetrying(null);
  };
  const rpDeleteMsg=async(inst,entryId)=>{
    if(!window.confirm(t("meldung.eintragLoeschenFrage"))) return;
    try{await api(`/api/${inst.id}/reprocess/entry/${encodeURIComponent(entryId)}`,{method:"DELETE"});
      setRpFailedMsgs(p=>p.filter(m=>m.id!==entryId));}catch(e){window.alert(t("meldung.loeschenFehler",{meldung:e.message||e}));}
  };
  const saveEdit=async()=>{
    if(!editAlert)return;setSaving(true);
    try{
      const emails=editEmails.split(",").map(e=>e.trim()).filter(Boolean);
      const channels={
        email:{enabled:editChEmail,recipients:emails,useAi:editUseAi,subjectTemplate:editSubjectTpl,htmlFormat:editHtmlFormat,resolvedNotify:editResolvedNotify},
        servicenow:{enabled:editChSnow,category:editSnowCategory,subcategory:editSnowSubcat,assignment_group:editSnowGroup,impact:editSnowImpact,urgency:editSnowUrgency,caller_id:editSnowCaller}
      };
      const payload={emails,useAi:editUseAi,customText:editCustomText,severity:editSeverity,
        evalWindowMin:editEvalWindow,reNotifyMin:editReNotify,autoResolveMin:editAutoResolve,channels};
      if(editAlert.isNew){
        const created=await api(`/api/${editAlert.instanceId}/alerts`,{method:"POST",body:JSON.stringify({iflowName:editAlert.iflowName,iflowId:editAlert.iflowId,...payload})});
        created.instanceName=editAlert.instanceName;created.spaceName=editAlert.spaceName;created.tenantUrl=editAlert.tenantUrl;
        setAlerts(p=>[...p,created]);
      }else{
        await api(`/api/${editAlert.instanceId}/alerts/${editAlert.id}`,{method:"PUT",body:JSON.stringify(payload)});
        setAlerts(p=>p.map(a=>a.id===editAlert.id?{...a,...payload}:a));
      }
      setEditAlert(null);
    }catch(e){window.alert(t("meldung.fehler",{meldung:e.message}));}
    setSaving(false);
  };

  // ServiceNow echte Auswahlwerte laden (einmalig, wenn der Kanal geoeffnet wird)
  const loadSnowChoices=async()=>{
    if(snowChoices||snowChoicesLoading)return;
    setSnowChoicesLoading(true);
    try{const r=await api("/api/servicenow/choices");setSnowChoices(r);}catch{setSnowChoices(null);}
    setSnowChoicesLoading(false);
  };

  // === Neuer-Alert Wizard ===
  const awSelectInst=async(inst)=>{
    setAwInst(inst);setAwPackages([]);setAwSearch("");setAwLoadingPkgs(true);
    try{
      const pkgs=await api(`/api/${inst.id}/content/packages`);
      const withFlows=[];
      for(const pkg of pkgs){
        try{const flows=await api(`/api/${inst.id}/content/packages/${encodeURIComponent(pkg.Id)}/flows`);withFlows.push({...pkg,flows:flows||[]});}
        catch{withFlows.push({...pkg,flows:[]});}
      }
      setAwPackages(withFlows);
    }catch(e){console.error(e);}
    setAwLoadingPkgs(false);
  };
  const closeWizard=()=>{setAwOpen(false);setAwInst(null);setAwPackages([]);setAwSearch("");};
  const startNewAlert=(inst,flow)=>{
    const exists=alerts.some(a=>a.instanceId===inst.id&&(a.iflowId===flow.Id||a.iflowName===(flow.Name||flow.Id)));
    if(exists&&!window.confirm(t("meldung.alertExistiert",{name:flow.Name||flow.Id})))return;
    closeWizard();
    setEditEmails("");setEditUseAi(true);setEditCustomText("");
    setEditSeverity("critical");setEditEvalWindow(15);setEditReNotify(30);setEditAutoResolve(60);
    setEditChEmail(true);setEditChEmailOpen(true);
    setEditChSnow(false);setEditChSnowOpen(false);
    setEditSnowCategory("");setEditSnowSubcat("");setEditSnowGroup("");setEditSnowImpact("2");setEditSnowUrgency("2");setEditSnowCaller("");
    setEditResolvedNotify(true);setEditHtmlFormat(true);
    setEditSubjectTpl("[{severity}] {iflow_name} - {error_count} Fehler");
    setEditAlert({isNew:true,instanceId:inst.id,instanceName:inst.name,spaceName:inst.spaceName,tenantUrl:inst.tenantUrl,iflowName:flow.Name||flow.Id,iflowId:flow.Id});
  };

  const grouped={};
  alerts.forEach(a=>{const key=`${a.instanceName} (${a.spaceName})`;(grouped[key]=grouped[key]||[]).push(a);});
  const activeCount=alerts.filter(a=>a.enabled).length;
  const pausedCount=alerts.filter(a=>!a.enabled).length;

  return(
    <div className="iio">
      {/* Page Head */}
      <div className="page-head">
        <div>
          <h1>{t("kopf.titel")}</h1>
          <div className="page-subtitle">{t("kopf.aktiv",{count:activeCount})} &middot; {t("kopf.pausiert",{count:pausedCount})} &middot; {t("gemeinsam:einheit.instanz",{count:instances.length})}</div>
        </div>
        {tab==="alerts"&&!loading&&(
          <button className="btn btn--primary" onClick={()=>{setAwOpen(true);setAwInst(null);setAwPackages([]);setAwSearch("");}} disabled={instances.length===0} title={instances.length===0?t("kopf.keineInstanzen"):t("kopf.neuerAlertTitel")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:"-2px"}}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {t("kopf.neuerAlert")}
          </button>
        )}
      </div>

      {/* KPI Row */}
      {tab==="alerts"&&!loading&&alerts.length>0&&(
        <div className="kpi-row">
          <div className="kpi-card">
            <div className="kpi-label">{t("kpi.aktiveAlerts")}</div>
            <div className="kpi-value" style={{color:"var(--iio-success)"}}>{activeCount}</div>
            <div className="kpi-meta"><span className="status status--ok">{t("kpi.aktiveAlertsMeta")}</span></div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{t("kpi.pausierteAlerts")}</div>
            <div className="kpi-value" style={{color:"var(--iio-text-3)"}}>{pausedCount}</div>
            <div className="kpi-meta"><span className="status status--idle">{t("kpi.pausierteAlertsMeta")}</span></div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{t("kpi.instanzen")}</div>
            <div className="kpi-value">{instances.length}</div>
            <div className="kpi-meta"><span style={{fontSize:12,color:"var(--iio-text-3)"}}>{instances.map(i=>i.name).join(", ")}</span></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-bar">
        {/* k bleibt woertlich: es ist der Zustandswert, nicht die Beschriftung. */}
        {[{k:"alerts",l:t("reiter.alerts")},{k:"reprocessing",l:t("reiter.reprocessing")}].map(tb=>(
          <button key={tb.k} className={`tab-btn${tab===tb.k?" active":""}`} onClick={()=>setTab(tb.k)}>{tb.l}</button>
        ))}
      </div>

      {tab==="alerts"&&<div>
        {loading?<div className="empty-state"><div className="desc">{t("liste.laedt")}</div></div>
        :alerts.length===0?<div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          <div className="title">{t("liste.leerTitel")}</div>
          <div className="desc">{t("liste.leerText")}</div>
          <button className="btn btn--primary" style={{marginTop:"var(--iio-s-4)"}} onClick={()=>{setAwOpen(true);setAwInst(null);setAwPackages([]);setAwSearch("");}} disabled={instances.length===0}>{t("kopf.neuerAlert")}</button>
        </div>
        :Object.entries(grouped).map(([instKey,instAlerts])=>(
          <div key={instKey} className="inst-section">
            <div className="inst-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iio-brand-ink)" strokeWidth="1.5" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              <span>{instKey}</span>
              <span className="inst-count">{t("liste.anzahl",{count:instAlerts.length})}</span>
            </div>
            {instAlerts.map(al=>(
              <div key={al.id} className="alert-row">
                <div className={`alert-status-bar ${al.enabled?"active":"paused"}`}/>
                <div className="alert-body">
                  <div className="alert-name">{al.iflowName}</div>
                  <div className="alert-sub">
                    {al.iflowId&&al.iflowId!==al.iflowName?<span style={{fontSize:10,color:"var(--iio-text-3)",marginRight:8}}>{al.iflowId}</span>:null}
                    {al.emails.join(", ")}
                  </div>
                  <div className="alert-meta">
                    {al.useAi&&<>{t("liste.kiAnalyse")} &middot; </>}
                    {al.customText&&<>{t("liste.eigenerText")} &middot; </>}
                    <span style={{color:al.enabled?"var(--iio-success)":"var(--iio-text-3)"}}>{al.enabled?t("liste.aktiv"):t("liste.pausiert")}</span>
                  </div>
                </div>
                <div className="alert-actions">
                  <button className="btn btn--secondary btn--sm" onClick={()=>openEdit(al)}>{t("gemeinsam:aktion.bearbeiten")}</button>
                  <button className={`btn btn--secondary btn--sm${!al.enabled?" btn--activate":""}`} onClick={()=>toggleAlert(al)}>{al.enabled?t("liste.pausieren"):t("liste.aktivieren")}</button>
                  <button className="btn btn--destructive btn--sm" onClick={()=>deleteAlert(al)}>{t("gemeinsam:aktion.loeschen")}</button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>}

      {tab==="reprocessing"&&<div>
        {/* Sub-Navigation */}
        <div className="subtab-bar">
          {[{k:"setup",l:t("rp.reiterSetup")},{k:"failed",l:t("rp.reiterFehler")}].map(t2=>(
            <button key={t2.k} className={`subtab-btn${rpView===t2.k?" active":""}`} onClick={()=>setRpView(t2.k)}>{t2.l}</button>
          ))}
        </div>

        {/* === SETUP WIZARD === */}
        {rpView==="setup"&&<div>
          {!rpWizard?(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div className="rp-desc">
                  <Trans t={t} i18nKey="rp.patternBeschreibung" components={[<b/>,<code/>]}/>
                </div>
                <button className="btn btn--signal" onClick={()=>{setRpWizard(true);setRpInst(null);setRpPackages([]);setRpStatusCache({});setRpDryRunResult(null);setRpInjectResult(null);}}>{t("rp.einrichtenKnopf")}</button>
              </div>
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" style={{opacity:0.5}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                <div className="title">{t("rp.setupLeerTitel")}</div>
                <div className="desc">{t("rp.setupLeerText")}</div>
              </div>
            </div>
          ):(
            <div>
              <button className="btn btn--tertiary btn--sm" onClick={()=>setRpWizard(false)} style={{marginBottom:16}}>&larr; {t("gemeinsam:aktion.zurueck")}</button>
              <h3 style={{fontWeight:700,fontSize:18,color:"var(--iio-text-1)",margin:"0 0 16px"}}>{t("rp.wizardTitel")}</h3>

              {/* Step 1: Instanz */}
              <div className="step-label">{t("rp.schritt1")}</div>
              <div className="inst-btn-row">
                {instances.map(inst=>(
                  <button key={inst.id} className={`inst-btn${rpInst?.id===inst.id?" active":""}`} onClick={()=>rpSelectInst(inst)}>
                    {inst.name} <span className="region">{inst.spaceName}</span>
                  </button>
                ))}
              </div>

              {/* Step 2: iFlow */}
              {rpInst&&<div>
                <div className="step-label">{t("rp.schritt2")}</div>
                {rpLoadingPkgs?<div className="empty-state"><div className="desc">{t("rp.ladePackages")}</div></div>
                :rpPackages.length===0?<div style={{padding:20,color:"var(--iio-text-3)",fontSize:12}}>{t("rp.keinePackages")}</div>
                :rpPackages.map(pkg=>(
                  <div key={pkg.Id} className="pkg-acc">
                    <button className={`pkg-head${rpExpandedPkg===pkg.Id?" open":""}`} onClick={()=>setRpExpandedPkg(rpExpandedPkg===pkg.Id?null:pkg.Id)}>
                      <span>{pkg.Name||pkg.Id}</span>
                      <span style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:10,color:"var(--iio-text-3)",fontWeight:400}}>{t("rp.flowAnzahl",{count:pkg.flows?.length||0})}</span>
                        <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </span>
                    </button>
                    {rpExpandedPkg===pkg.Id&&pkg.flows?.length>0&&(
                      <div className="pkg-body">
                        {pkg.flows.map(flow=>{
                          const st=rpStatusCache[flow.Id];const isInj=rpInjecting===flow.Id;
                          return(
                            <div key={flow.Id} className="flow-row">
                              <div className="flow-info">
                                <div className="flow-name">{flow.Name}</div>
                                <div className="flow-id">{flow.Id}</div>
                              </div>
                              {st&&<span className={`tag ${st.injected?"tag--ok":"tag--default"}`}>
                                {st.injected&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" style={{marginRight:4}}><polyline points="20 6 9 17 4 12"/></svg>}
                                {st.injected?t("rp.patternAktiv"):t("rp.nichtEingerichtet")}</span>}
                              <div style={{display:"flex",gap:4,flexShrink:0}}>
                                {!st&&<button className="btn btn--secondary btn--sm" onClick={()=>rpCheckStatus(rpInst,flow.Id)}>{t("rp.pruefen")}</button>}
                                <button className="btn btn--secondary btn--sm" disabled={rpDownloading===flow.Id} onClick={()=>rpDownloadZip(rpInst,flow.Id,flow.Name)}>{rpDownloading===flow.Id?t("rp.laden"):t("rp.zip")}</button>
                                {(!st||!st.injected)&&<button className="btn btn--signal btn--sm" disabled={isInj} onClick={()=>rpDryRun(rpInst,flow.Id)}>{isInj?t("rp.analysiere"):t("rp.patternEinfuegen")}</button>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>}
            </div>
          )}

          {/* Dry-Run Result Modal */}
          {rpDryRunResult&&<div className={`modal-overlay${rpDryRunResult?" open":""}`} onClick={()=>setRpDryRunResult(null)}>
            <div className="modal" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
              <div className="modal-banner">
                <h3 style={{display:"flex",alignItems:"center",gap:10}}>
                  {rpDryRunResult.success?<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--iio-success)" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  :<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--iio-critical)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
                  {rpDryRunResult.success?t("rp.dryRunOk"):t("rp.dryRunFehlgeschlagen")}
                </h3>
                <div className="spacer"/>
                <button className="x" onClick={()=>setRpDryRunResult(null)}>&times;</button>
              </div>
              <div className="modal-body">
                {rpDryRunResult.success&&<div>
                  <div style={{fontSize:13,color:"var(--iio-text-2)",marginBottom:12}}>{t("rp.dryRunEinleitung")}</div>
                  <div style={{background:"var(--iio-bg-1)",borderRadius:8,padding:12,marginBottom:16}}>
                    {rpDryRunResult.report?.steps?.map((s,i)=>(<div key={i} style={{fontSize:12,color:"var(--iio-text-2)",padding:"4px 0",lineHeight:1.6,display:"flex",gap:8,alignItems:"flex-start"}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iio-success)" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}><polyline points="20 6 9 17 4 12"/></svg><span>{s}</span></div>))}
                  </div>
                  {rpDryRunResult.report?.warnings?.length>0&&<div style={{background:"rgba(200,54,46,.08)",borderRadius:8,padding:12,marginBottom:16}}>
                    {rpDryRunResult.report.warnings.map((w,i)=>(<div key={i} style={{fontSize:12,color:"var(--iio-critical)",padding:"4px 0",display:"flex",gap:8,alignItems:"flex-start"}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iio-critical)" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg><span>{w}</span></div>))}
                  </div>}
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center"}}>
                    <label className="check-row" style={{marginRight:"auto",marginBottom:0}}>
                      <input type="checkbox" checked={rpAutoDeploy} onChange={e=>setRpAutoDeploy(e.target.checked)}/>
                      {t("rp.autoDeploy")}
                    </label>
                    <button className="btn btn--secondary" onClick={()=>setRpDryRunResult(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
                    <button className="btn btn--signal" onClick={()=>{const fid=rpDryRunResult.flowId;const dep=rpAutoDeploy;setRpDryRunResult(null);rpInject(rpInst,fid,dep);}}>{rpAutoDeploy?t("rp.jetztInjizierenDeployen"):t("rp.jetztInjizieren")}</button>
                  </div>
                </div>}
                {!rpDryRunResult.success&&<div>
                  <div className="fail-error-box">{rpDryRunResult.error||t("rp.unbekannterFehler")}{rpDryRunResult.message&&<div style={{marginTop:8,color:"var(--iio-text-2)"}}>{rpDryRunResult.message}</div>}</div>
                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}><button className="btn btn--secondary" onClick={()=>setRpDryRunResult(null)}>{t("gemeinsam:aktion.schliessen")}</button></div>
                </div>}
              </div>
            </div>
          </div>}

          {/* Inject Result Toast */}
          {rpInjectResult&&<div className={`toast ${rpInjectResult.success?"toast--ok":"toast--fail"}`} onClick={()=>setRpInjectResult(null)}>
            <div style={{fontWeight:700,marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
              {rpInjectResult.success?<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iio-success)" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              :<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iio-critical)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
              {rpInjectResult.success?t("rp.injectOk"):t("rp.injectFehlgeschlagen")}
            </div>
            <div style={{fontSize:12,color:"var(--iio-text-3)"}}>{rpInjectResult.success?rpInjectResult.message:rpInjectResult.error}</div>
            {rpInjectResult.report?.warnings?.length>0&&<div style={{fontSize:11,marginTop:4,color:"var(--iio-critical)"}}>{rpInjectResult.report.warnings.join(" | ")}</div>}
          </div>}
        </div>}

        {/* === FAILED MESSAGES === */}
        {rpView==="failed"&&<div>
          {!rpFailedInst?(
            <div>
              <div style={{fontSize:13,color:"var(--iio-text-3)",marginBottom:16}}><Trans t={t} i18nKey="rp.failedEinleitung" components={[<code style={{background:"var(--iio-bg-3)",padding:"1px 5px",borderRadius:2,fontSize:11}}/>]}/></div>
              <div className="inst-btn-row">
                {instances.map(inst=>(
                  <button key={inst.id} className="inst-btn" onClick={()=>rpLoadFailed(inst)}>
                    {inst.name} <span className="region">{inst.spaceName}</span>
                  </button>
                ))}
              </div>
            </div>
          ):(
            <div>
              <div className="failed-header">
                <div>
                  <div className="failed-title">{t("rp.failedTitel")}</div>
                  <div className="failed-sub">{t("rp.failedSub",{instanz:rpFailedInst.name,count:rpFailedMsgs.length})}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn--secondary btn--sm" onClick={()=>rpLoadFailed(rpFailedInst)}>{t("gemeinsam:aktion.aktualisieren")}</button>
                  <button className="btn btn--tertiary btn--sm" onClick={()=>{setRpFailedInst(null);setRpFailedMsgs([]);}}>&larr; {t("gemeinsam:aktion.zurueck")}</button>
                </div>
              </div>

              {rpLoadingFailed&&<div className="empty-state"><div className="desc">{t("rp.ladeFailed")}</div></div>}

              {!rpLoadingFailed&&rpFailedMsgs.length===0&&<div className="empty-state">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" style={{opacity:.5}}><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <div className="title">{t("rp.keineFailedTitel")}</div>
                <div className="desc">{t("rp.keineFailedText")}</div>
              </div>}

              {rpFailedMsgs.map(msg=>(
                <div key={msg.id} className="fail-row">
                  <div className="fail-head" onClick={()=>setRpExpandedMsg(rpExpandedMsg===msg.id?null:msg.id)}>
                    <div className="fail-body">
                      <div className={`fail-name${rpExpandedMsg===msg.id?" open":""}`}>
                        <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                        {msg.iFlowId||t("rp.unbekannterIflow")}</div>
                      <div className="fail-sub">
                        {msg.failedAt?new Date(msg.failedAt).toLocaleString(gebiet):t("rp.unbekannt")}
                        {msg.messageId&&<span> &middot; {t("rp.mpl",{id:msg.messageId.substring(0,16)})}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      <button className="btn btn--secondary btn--sm" onClick={()=>{
                        const text=msg.body||"";
                        navigator.clipboard.writeText(text).then(()=>{
                          const el=document.getElementById("cp_"+msg.id);if(el){el.textContent=t("rp.kopiert");setTimeout(()=>{el.textContent=t("rp.payloadKopieren")},1500);}
                        }).catch(()=>window.alert(t("meldung.kopierenFehler")));
                      }}><span id={"cp_"+msg.id}>{t("rp.payloadKopieren")}</span></button>
                      <button className="btn btn--destructive btn--sm" onClick={()=>rpDeleteMsg(rpFailedInst,msg.id)}>{t("rp.verwerfen")}</button>
                    </div>
                  </div>
                  {rpExpandedMsg===msg.id&&<div className="fail-detail">
                    <div style={{marginBottom:12}}>
                      <div className="fail-detail-label">{t("rp.fehlermeldung")}</div>
                      <div className="fail-error-box">{msg.error||t("rp.keinFehler")}</div>
                    </div>
                    {msg.stackTrace&&<details style={{marginBottom:12}}>
                      <summary style={{cursor:"pointer",color:"var(--iio-text-2)",fontSize:12}}>{t("rp.stackTrace")}</summary>
                      <div className="fail-payload-box" style={{marginTop:4,fontSize:10,maxHeight:200}}>{msg.stackTrace}</div>
                    </details>}
                    <div style={{marginBottom:12}}>
                      <div className="fail-detail-label">{t("rp.originalPayload",{typ:msg.contentType||t("rp.typUnbekannt")})}</div>
                      <div className="fail-payload-box">
                        {msg.body?(msg.body.length>5000?msg.body.substring(0,5000)+t("rp.abgeschnitten"):msg.body):t("rp.keinPayload")}
                      </div>
                    </div>
                    {msg.headers&&<details style={{marginBottom:8}}>
                      <summary style={{cursor:"pointer",color:"var(--iio-text-2)",fontSize:12}}>{t("rp.originalHeaders")}</summary>
                      <div className="fail-payload-box" style={{marginTop:4,maxHeight:150}}>
                        {(()=>{try{return JSON.stringify(JSON.parse(msg.headers),null,2);}catch{return msg.headers;}})()}
                      </div>
                    </details>}
                    <div style={{fontSize:11,color:"var(--iio-text-3)",marginTop:8}}>{t("rp.entryId")} <code>{msg.id}</code></div>
                  </div>}
                </div>
              ))}
            </div>
          )}
        </div>}

      </div>}

      {/* Edit Modal */}
      {/* === Neuer-Alert Wizard: Instanz -> iFlow === */}
      {awOpen&&(
        <div className="modal-overlay open" onClick={closeWizard}>
          <div className="modal alrt-modal sev-info" style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
            <div className="modal-banner">
              <h3>{t("wizard.titel")}</h3>
              <div className="spacer"/>
              <button className="x" onClick={closeWizard} aria-label={t("gemeinsam:aktion.schliessen")}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:12,color:"var(--iio-text-3)",marginBottom:"var(--iio-s-4)"}}>{t("wizard.einleitung")}</div>
              <div className="field">
                <label>{t("wizard.instanz")}</label>
                <select className="input" value={awInst?.id||""} onChange={e=>{const i=instances.find(x=>x.id===e.target.value);if(i){awSelectInst(i);}else{setAwInst(null);setAwPackages([]);}}}>
                  <option value="">{t("wizard.instanzWaehlen")}</option>
                  {instances.map(i=><option key={i.id} value={i.id}>{i.name} ({i.spaceName})</option>)}
                </select>
              </div>
              {awInst&&(
                <div style={{marginTop:"var(--iio-s-4)"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em",color:"var(--iio-text-3)",marginBottom:6}}>{t("wizard.iflow")}</label>
                  {awLoadingPkgs?(
                    <div className="aw-skel">{[0,1,2,3,4].map(i=><div key={i} className="aw-skel-row"><div className="aw-skel-line w1"/><div className="aw-skel-line w2"/></div>)}</div>
                  ):(()=>{
                    const flat=[];
                    awPackages.forEach(p=>(p.flows||[]).forEach(f=>flat.push({Id:f.Id,Name:f.Name,_pkg:p.Name||p.Id})));
                    const q=awSearch.toLowerCase().trim();
                    const shown=flat.filter(f=>!q||(f.Name||f.Id||"").toLowerCase().includes(q)||(f._pkg||"").toLowerCase().includes(q));
                    return(<>
                      <input className="input" placeholder={t("wizard.suche")} value={awSearch} onChange={e=>setAwSearch(e.target.value)} style={{marginBottom:8}}/>
                      <div style={{maxHeight:300,overflowY:"auto",border:"1px solid var(--iio-border)",borderRadius:"var(--iio-r-md)",background:"var(--iio-bg-2)"}}>
                        {shown.length===0?<div style={{padding:18,textAlign:"center",fontSize:13,color:"var(--iio-text-3)"}}>{t("wizard.keineFlows")}</div>:
                         shown.map(f=>{
                           const added=alerts.some(a=>a.instanceId===awInst.id&&(a.iflowId===f.Id||a.iflowName===(f.Name||f.Id)));
                           return(
                             <div key={f.Id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderBottom:"1px solid var(--iio-border-subtle)"}}>
                               <div style={{flex:1,minWidth:0}}>
                                 <div style={{fontSize:13,color:"var(--iio-text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.Name||f.Id}{added&&<span style={{fontSize:10,color:"var(--iio-text-3)",marginLeft:8}}>{t("wizard.bereitsUeberwacht")}</span>}</div>
                                 <div style={{fontSize:11,color:"var(--iio-text-3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f._pkg}</div>
                               </div>
                               <button className="btn btn--secondary btn--sm" onClick={()=>startNewAlert(awInst,f)}>{t("wizard.auswaehlen")}</button>
                             </div>
                           );
                         })}
                      </div>
                    </>);
                  })()}
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn--secondary" onClick={closeWizard}>{t("gemeinsam:aktion.abbrechen")}</button>
            </div>
          </div>
        </div>
      )}

      {editAlert&&(
        <div className="modal-overlay open" onClick={()=>setEditAlert(null)}>
          <div className={`modal alrt-modal sev-${editSeverity}`} style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
            {/* Banner mit Severity-Icon + iFlow-Headline */}
            <div className="modal-banner alrt-banner">
              <span className={`alrt-banner-ico sev-${editSeverity}`}>{SEV_ICONS[editSeverity]}</span>
              <div className="alrt-banner-txt">
                <h3>{editAlert.iflowName||(editAlert.isNew?t("edit.neu"):t("edit.bestehend"))}</h3>
                <div className="alrt-banner-sub">{editAlert.isNew?t("edit.neu"):t("edit.bestehend")} &middot; {editAlert.instanceName}</div>
              </div>
              <div className="spacer"/>
              {/* value bleibt woertlich: es geht so an den Server. */}
              <select className={`sev-select ${editSeverity}`} value={editSeverity} onChange={e=>setEditSeverity(e.target.value)}>
                <option value="critical">{t("severity.critical")}</option><option value="warning">{t("severity.warning")}</option><option value="info">{t("severity.info")}</option>
              </select>
              <button className="x" onClick={()=>setEditAlert(null)} aria-label={t("gemeinsam:aktion.schliessen")}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="alrt-sechead">{t("edit.sektionZeit")}</div>

              {/* Timing Metrics */}
              <div className="timing-row">
                {/* v bleibt die Minutenzahl fuer den Server, l ist nur die Beschriftung. */}
                {[{l:t("zeit.auswertungsfenster"),ico:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>,val:editEvalWindow,s:setEditEvalWindow,opts:[{v:5,l:min(5)},{v:15,l:min(15)},{v:30,l:min(30)},{v:60,l:std(1)}]},
                  {l:t("zeit.erneuteBenachrichtigung"),ico:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,val:editReNotify,s:setEditReNotify,opts:[{v:15,l:min(15)},{v:30,l:min(30)},{v:60,l:std(1)},{v:120,l:std(2)},{v:240,l:std(4)},{v:720,l:std(12)}]},
                  {l:t("zeit.automatischSchliessen"),ico:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,val:editAutoResolve,s:setEditAutoResolve,opts:[{v:30,l:min(30)},{v:60,l:std(1)},{v:120,l:std(2)},{v:240,l:std(4)}]}
                ].map((m,i)=>(
                  <div key={i} className="timing-card">
                    <div className="timing-label">{m.ico}<span>{m.l}</span></div>
                    <select value={m.val} onChange={e=>m.s(parseInt(e.target.value))}>
                      {m.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="alrt-sechead">{t("edit.sektionKanaele")}</div>

              {/* === CHANNEL: E-Mail === */}
              <div className={`ch-acc${editChEmail?" active-ch":""}`}>
                <div className="ch-head" onClick={()=>setEditChEmailOpen(!editChEmailOpen)}>
                  <div className="ch-left">
                    <input type="checkbox" checked={editChEmail} onChange={e=>{e.stopPropagation();setEditChEmail(e.target.checked);}} style={{width:14,height:14,accentColor:"var(--iio-brand-ink)"}} onClick={e=>e.stopPropagation()}/>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={editChEmail?"var(--iio-brand-ink)":"var(--iio-text-3)"} strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--iio-text-1)"}}>{t("email.titel")}</div>
                      {!editChEmailOpen&&editChEmail&&<div className="ch-preview">{t(editUseAi?"email.vorschauKi":"email.vorschau",{count:editEmails.split(",").filter(Boolean).length})}</div>}
                    </div>
                  </div>
                  <div className="ch-right">
                    {editChEmail&&<span className="status status--ok" style={{fontSize:11}}>{t("email.aktiv")}</span>}
                    <span style={{fontSize:11,color:"var(--iio-text-3)",transition:"transform .2s",transform:editChEmailOpen?"rotate(180deg)":"none"}}>&#9662;</span>
                  </div>
                </div>
                {editChEmailOpen&&<div className="ch-body">
                  <div className="field">
                    <label>{t("email.empfaenger")}</label>
                    <input className="input" value={editEmails} onChange={e=>setEditEmails(e.target.value)}/>
                  </div>
                  <div className="field">
                    <label>{t("email.betreff")}</label>
                    <input className="input" value={editSubjectTpl} onChange={e=>setEditSubjectTpl(e.target.value)}/>
                    {/* Die Platzhalter selbst bleiben woertlich: sie stehen so in der Vorlage. */}
                    <div className="placeholder-hint">{t("email.platzhalterHinweis")} <span className="ph-chip">{"{iflow_name}"}</span> {t("email.platzhalterIflow")}, <span className="ph-chip">{"{error_count}"}</span> {t("email.platzhalterAnzahl")}, <span className="ph-chip">{"{severity}"}</span> {t("email.platzhalterSeverity")}, <span className="ph-chip">{"{timeframe}"}</span> {t("email.platzhalterZeitfenster")}.</div>
                    {(()=>{
                      const sevL=t(`severity.${editSeverity}`,{defaultValue:t("severity.info")});
                      const subj=(editSubjectTpl||"").replace(/\{iflow_name\}/g,editAlert.iflowName||t("email.iflowFallback")).replace(/\{error_count\}/g,"3").replace(/\{severity\}/g,sevL).replace(/\{timeframe\}/g,min(editEvalWindow)).trim()||t("email.leererBetreff");
                      const snippet=(editCustomText||"").trim()||t("email.vorschauText",{iflow:editAlert.iflowName||t("email.iflowFallback"),minuten:editEvalWindow});
                      return (
                        <div className={`mail-preview sev-${editSeverity}`}>
                          <div className="mp-bar"/>
                          <div className="mp-head"><span className="mp-dot"/><span className="mp-from">Integration Monitor</span><span className="mp-time">{t("email.vorschauMarke")}</span></div>
                          <div className="mp-subj">{subj}</div>
                          <div className="mp-snippet">{snippet}</div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Digest Flow Visualization */}
                  <div className="digest-flow">
                    <div className="digest-step">
                      <div className="ds-dot"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></div>
                      <div className="step-label">{t("digest.ersterFehler")}</div>
                      <div className="step-value">{t("digest.ersterFehlerWert")}</div>
                    </div>
                    <div className="digest-arrow">{ARROW}{ARROW}</div>
                    <div className="digest-step">
                      <div className="ds-dot"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg></div>
                      <div className="step-label">{t("digest.sammelphase")}</div>
                      <div className="step-value">{t("digest.sammelphaseWert")}</div>
                    </div>
                    <div className="digest-arrow">{ARROW}{ARROW}</div>
                    <div className="digest-step">
                      <div className="ds-dot"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="8" y1="18" x2="20" y2="18"/><line x1="4" y1="6" x2="4.01" y2="6"/><line x1="4" y1="12" x2="4.01" y2="12"/><line x1="4" y1="18" x2="4.01" y2="18"/></svg></div>
                      <div className="step-label">{t("digest.zusammenfassung")}</div>
                      <div className="step-value">{t("digest.zusammenfassungWert")}</div>
                    </div>
                  </div>

                  <div style={{marginTop:12}}>
                    <label className="check-row"><input type="checkbox" checked={editUseAi} onChange={e=>setEditUseAi(e.target.checked)}/> {t("email.kiFehleranalyse")}</label>
                    <label className="check-row"><input type="checkbox" checked={editResolvedNotify} onChange={e=>setEditResolvedNotify(e.target.checked)}/> {t("email.resolvedSenden")}</label>
                    <label className="check-row"><input type="checkbox" checked={editHtmlFormat} onChange={e=>setEditHtmlFormat(e.target.checked)}/> {t("email.htmlFormat")}</label>
                  </div>

                  <div className="field" style={{marginTop:12}}>
                    <label>{t("email.eigenerText")}</label>
                    <textarea className="input" rows={2} value={editCustomText} onChange={e=>setEditCustomText(e.target.value)} placeholder={t("email.eigenerTextPlatzhalter")}/>
                  </div>
                </div>}
              </div>

              {/* === CHANNEL: ServiceNow === */}
              <div className={`ch-acc${editChSnow?" active-ch":""}`}>
                <div className="ch-head" onClick={()=>{const open=!editChSnowOpen;setEditChSnowOpen(open);if(open)loadSnowChoices();}}>
                  <div className="ch-left">
                    <input type="checkbox" checked={editChSnow} onChange={e=>{e.stopPropagation();setEditChSnow(e.target.checked);if(e.target.checked){setEditChSnowOpen(true);loadSnowChoices();}}} style={{width:14,height:14,accentColor:"var(--iio-brand-ink)"}} onClick={e=>e.stopPropagation()}/>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={editChSnow?"var(--iio-brand-ink)":"var(--iio-text-3)"} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--iio-text-1)"}}>{t("snow.titel")}</div>
                      {!editChSnowOpen&&editChSnow&&<div className="ch-preview">{t("snow.vorschau",{kategorie:editSnowCategory,gruppe:editSnowGroup||t("snow.gruppeStandard")})}</div>}
                    </div>
                  </div>
                  <div className="ch-right">
                    {editChSnow&&<span className="status status--ok" style={{fontSize:11}}>{t("snow.aktiv")}</span>}
                    <span style={{fontSize:11,color:"var(--iio-text-3)",transition:"transform .2s",transform:editChSnowOpen?"rotate(180deg)":"none"}}>&#9662;</span>
                  </div>
                </div>
                {editChSnowOpen&&<div className="ch-body">
                  <div className="info-box">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    {t("snow.hinweisZugangsdaten")}
                  </div>
                  <div className="grid-2">
                    <div className="field">
                      <label>{t("snow.kategorie")}</label>
                      {/* Die Werte sind die OOB-Choice-Values und bleiben woertlich. */}
                      <select className="input" value={editSnowCategory} onChange={e=>setEditSnowCategory(e.target.value)}>
                        <option value="">{t("snow.kategorieKeine")}</option>
                        {(snowChoices?.categories?.length?snowChoices.categories:SNOW_OOB_CATEGORIES).map(c=><option key={c.value} value={c.value}>{snowKatLabel(c)}</option>)}
                        {editSnowCategory&&!(snowChoices?.categories?.length?snowChoices.categories:SNOW_OOB_CATEGORIES).some(c=>c.value===editSnowCategory)&&<option value={editSnowCategory}>{editSnowCategory}</option>}
                      </select>
                      {snowChoicesLoading&&<div style={{fontSize:10,color:"var(--iio-text-3)",marginTop:2}}>{t("snow.kategorienLaden")}</div>}
                      {snowChoices&&!snowChoices.categories?.length&&<div style={{fontSize:10,color:"var(--iio-text-3)",marginTop:2}}>{t("snow.kategorienFallback")}</div>}
                    </div>
                    <div className="field">
                      <label>{t("snow.unterkategorie")}</label>
                      <input className="input" value={editSnowSubcat} onChange={e=>setEditSnowSubcat(e.target.value)} placeholder={t("snow.unterkategoriePlatzhalter")}/>
                    </div>
                    <div className="field">
                      <label>{t("snow.zuweisungsgruppe")}</label>
                      <input className="input" list="snow-groups" value={editSnowGroup} onChange={e=>setEditSnowGroup(e.target.value)} placeholder="CPI Operations"/>
                      {snowChoices?.groups?.length>0&&<datalist id="snow-groups">{snowChoices.groups.map(g=><option key={g} value={g}/>)}</datalist>}
                    </div>
                    <div className="field">
                      <label>{t("snow.impact")}</label>
                      <select className="input" value={editSnowImpact} onChange={e=>setEditSnowImpact(e.target.value)}>
                        <option value="1">{t("snow.stufe.hoch")}</option><option value="2">{t("snow.stufe.mittel")}</option><option value="3">{t("snow.stufe.niedrig")}</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>{t("snow.dringlichkeit")}</label>
                      <select className="input" value={editSnowUrgency} onChange={e=>setEditSnowUrgency(e.target.value)}>
                        <option value="1">{t("snow.stufe.hoch")}</option><option value="2">{t("snow.stufe.mittel")}</option><option value="3">{t("snow.stufe.niedrig")}</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>{t("snow.caller")}</label>
                      <input className="input" value={editSnowCaller} onChange={e=>setEditSnowCaller(e.target.value)} placeholder="cpi-monitor"/>
                    </div>
                  </div>
                  <label className="check-row" style={{marginTop:4}}><input type="checkbox" checked={editUseAi} onChange={e=>setEditUseAi(e.target.checked)}/> {t("snow.kiAnalyse")}</label>
                </div>}
              </div>

              {/* === CHANNEL: Teams/Slack (placeholder) === */}
              <div className="ch-acc ch-disabled">
                <div className="ch-head">
                  <div className="ch-left">
                    <input type="checkbox" disabled style={{width:14,height:14}}/>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iio-text-3)" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--iio-text-3)"}}>Microsoft Teams / Slack</div>
                      <div style={{fontSize:10,color:"var(--iio-text-4)"}}>{t("demnaechst.teamsHinweis")}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* === CHANNEL: Webhook (placeholder) === */}
              <div className="ch-acc ch-disabled">
                <div className="ch-head">
                  <div className="ch-left">
                    <input type="checkbox" disabled style={{width:14,height:14}}/>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iio-text-3)" strokeWidth="1.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--iio-text-3)"}}>Custom Webhook</div>
                      <div style={{fontSize:10,color:"var(--iio-text-4)"}}>{t("demnaechst.webhookHinweis")}</div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="modal-foot">
              <button className="btn btn--secondary" onClick={()=>setEditAlert(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
              <button className="btn btn--signal" onClick={saveEdit} disabled={saving||(!editChEmail&&!editChSnow)}>{saving?(editAlert.isNew?t("edit.erstelleLaeuft"):t("edit.speichereLaeuft")):(editAlert.isNew?t("edit.erstellen"):t("edit.speichern"))}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN APP
// ============================================================

export default AlertingPage;
